import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAsync } from 'react-use';
import { withTheme } from '@emotion/react';
import { compact, head, map, uniq } from 'lodash';
import { TestId } from '@components/test-ids';
import { breakpoints } from '@styles/breakpoints';
import { TourDetails } from '@model/tours';
import { ToursApi } from '@model/iceberg/service/tours';
import { useI18NextContext, useResize } from '@components/hooks';
import { Page, TripTypes } from '@model/common';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg';
import { FiltersSideBarType } from '@model/filters';
import Cookies, { Cookie } from '@model/common/cookie/cookie';
import { resetSelectedFilters } from '@state/slices/filters/filterSlice';
import { clearToursByType } from '@state/slices/search/tours';
import { unsetLeadInTourReferences } from '@state/slices/tours/tour-references';
import { getIsAgent } from '@state/slices/agent/agentSelectors';
import { Carousel, getSlideDisplayCount } from '@components/common';
import { getSubtitle } from '@components/recently-viewed/card/ToursRecentlyViewedCard';
import { SkeletonElement, SkeletonVariant } from '@components/common/skeleton-loading';
import { TourProductTile } from './TourProductTile';
import { getImageUrl } from '@util/images';
import { Logging } from '@util/logging';

/* ***************** *
 *       Types       *
 * ***************** */

export interface TourProductGroupProps {
  testId?: string;
  tripType: TripTypes;
  path?: string;
  title: string;
  subTitle?: string;
  packages: Array<TourDetails>;
  ctaTitle?: string;
  ctaUrl?: string;
  hideCta?: boolean;
}

/* ******************************* *
 *    TourProductGroupComponent    *
 * ******************************* */

export const TourProductGroupComponent = ({
  title,
  subTitle,
  packages,
  ctaTitle,
  ctaUrl,
  hideCta,
  testId,
  tripType,
  path
}: TourProductGroupProps) => {
  const t: any = useI18NextContext();
  const dispatch = useDispatch();
  const resize = useResize();
  const isAgent: boolean = useSelector(getIsAgent);
  const authToken = isAgent ? new Cookies().get(Cookie.TRADE_AUTHORIZATION) : undefined;

  if (!packages?.length) {
    Logging.error({
      text: `[TourProductGroupComponent] Component did not receive the componentData and cannot render.`
    });
    return null;
  }

  const { value: tours } = useAsync(async () => {
    if (isAgent && authToken) {
      const api: ToursApi = new ToursApi();
      const tourDetails = await Promise.all(packages.map(({ path }) => api.getTourDetails(path, authToken)));
      return tourDetails.map(({ data: { tours } }) => tours[0]);
    } else {
      return packages;
    }
  }, [isAgent, path]);

  const displayPackages = isAgent ? tours : packages;

  if (!displayPackages) {
    return (
      <>
        <SkeletonElement variant={SkeletonVariant.HEADING} width={'100%'} height={100} />
        <SkeletonElement variant={SkeletonVariant.RECTANGLE} width={'100%'} height={500} />
        <SkeletonElement variant={SkeletonVariant.HEADING} width={'100%'} height={100} />
      </>
    );
  }

  const componentTripType = head(uniq(compact(map(displayPackages, 'tripType')))) || tripType;
  const linkTripType = tripType === TripTypes.PRIVATE_TOURS ? tripType : componentTripType;

  /* *** LOGIC *** */
  const getTileImageSize: () => number = () => {
    switch (displayPackages.length) {
      case 1:
        return breakpoints.large;
      case 2:
        return breakpoints.medium;
      default:
        return breakpoints.small;
    }
  };

  const slideCount = getSlideDisplayCount(resize, displayPackages.length);
  const sizes = `${Math.round(100 / slideCount)}vw`;

  /* *** RENDERERS *** */
  const renderTours = () =>
    displayPackages.map(({ name, price, image, path, days }: TourDetails) => {
      return (
        <TourProductTile
          key={name}
          name={name}
          imageUrl={getImageUrl(image, getTileImageSize())}
          path={path}
          tripType={linkTripType}
          price={price}
          subTitle={getSubtitle(days || 0, componentTripType, t)}
          sizes={sizes}
        />
      );
    });

  const linkBuilder: DestinationLinkBuilder = new DestinationLinkBuilder();
  const allToursLink: LinkItem | undefined = ctaUrl
    ? undefined
    : linkBuilder.getDestinationLink(path, linkTripType, Page.ALL_TOURS);

  const handleCtaClick = () => {
    dispatch(resetSelectedFilters(FiltersSideBarType.TOURS));
    dispatch(clearToursByType(tripType));
    dispatch(unsetLeadInTourReferences());
  };

  const label = ctaTitle;
  const linkHref = ctaUrl ? `${/^\//.test(ctaUrl) ? '' : '/'}${ctaUrl}` : undefined;
  const link =
    !hideCta && label && path ? { label, onClick: handleCtaClick, href: linkHref, linkItem: allToursLink } : undefined;

  return (
    <Carousel testId={testId || TestId.tourProductTile.group} title={title} subTitle={subTitle} link={link}>
      {renderTours()}
    </Carousel>
  );
};

export const TourProductGroup = withTheme(TourProductGroupComponent);
